import { post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import config from "../../config";

const API_NAME = "vc-support";
const SUPPORT_PATH = "/proposal-generator/support";
const LOCAL_DELAY_MS = 15;
const MAX_FILES_PER_ACTION = 5;
const MAX_FILE_BYTES = 6 * 1024 * 1024;
const MAX_TOTAL_FILE_BYTES = 6 * 1024 * 1024;

export const SUPPORT_STATUS_LABELS = {
  not_fixed: "Not Fixed",
  submitted: "Submitted",
  in_process: "In Process",
  scheduled_for_release: "Scheduled for Release",
  on_hold: "On Hold",
  completed: "Completed",
  closed: "Closed",
  waiting_for_information: "Waiting for Information",
  waiting_for_user_review: "Waiting for User Review",
};

export const SUPPORT_ENVIRONMENT_LABELS = {
  production: "Production",
  test: "TEST",
  legacy: "Legacy / Unspecified",
};

const SUPPORT_TICKET_STATUS_PRIORITY = {
  not_fixed: 0,
  submitted: 1,
  in_process: 2,
  scheduled_for_release: 3,
  on_hold: 4,
  completed: 5,
  closed: 6,
  waiting_for_information: 7,
  waiting_for_user_review: 8,
};

const AGENT_STATUSES = [
  "submitted",
  "in_process",
  "waiting_for_information",
  "scheduled_for_release",
  "waiting_for_user_review",
  "on_hold",
  "not_fixed",
  "closed",
];

const ALLOWED_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".xlsm",
  ".xlsb",
  ".csv",
  ".txt",
  ".rtf",
  ".ppt",
  ".pptx",
];

const localAttachmentUrls = new Map();
const remoteAttachmentUrls = new Map();
let localSequence = 1;
let localTickets = [];
let localActivities = [];
let localAttachments = [];
let localInitialized = false;

function clientDiagnostics() {
  return {
    frontend_version:
      (typeof window !== "undefined" && window.APP_VERSION) || "unknown",
    proposal_api_version: config.apiGateway.version || "unknown",
  };
}

function isLocalMode() {
  return (
    process.env.REACT_APP_SUPPORT_LOCAL_MODE === "true" ||
    (typeof window !== "undefined" &&
      window.__VICUS_SUPPORT_LOCAL_MODE__ === true)
  );
}

function localIdentity() {
  const supplied =
    typeof window !== "undefined"
      ? window.__VICUS_SUPPORT_IDENTITY__ || {}
      : {};
  return {
    user_id: supplied.user_id || "local-advisor",
    login: supplied.login || "local.advisor@vicuscapital.com",
    display_name: supplied.display_name || "Local Vicus Advisor",
    company_alias: supplied.company_alias || "Vicus Capital",
    has_access: supplied.has_access !== false,
    can_collaborate: supplied.can_collaborate === true,
    can_manage: supplied.can_manage === true,
  };
}

function nowIso() {
  return new Date().toISOString();
}

function compareSupportTickets(left, right) {
  const priorityDifference =
    (SUPPORT_TICKET_STATUS_PRIORITY[left.status] ?? 9) -
    (SUPPORT_TICKET_STATUS_PRIORITY[right.status] ?? 9);
  if (priorityDifference) return priorityDifference;

  const leftUpdatedAt = String(
    left.updated_at || left.last_activity_at || "",
  );
  const rightUpdatedAt = String(
    right.updated_at || right.last_activity_at || "",
  );
  const updatedAtDifference = rightUpdatedAt.localeCompare(leftUpdatedAt);
  if (updatedAtDifference) return updatedAtDifference;

  return Number(right.ticket_sequence || 0) - Number(left.ticket_sequence || 0);
}

function localId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replaceAll("-", "");
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`
    .padEnd(32, "0")
    .slice(0, 32);
}

function ticketNumber(value) {
  return `VIC-${String(Number(value || 0)).padStart(6, "0")}`;
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function localWait() {
  return new Promise((resolve) => setTimeout(resolve, LOCAL_DELAY_MS));
}

function localError(message, status = 400) {
  const error = new Error(message);
  error.status = status;
  throw error;
}

function validateFiles(files = []) {
  if (files.length > MAX_FILES_PER_ACTION) {
    localError(`Add no more than ${MAX_FILES_PER_ACTION} files at one time.`);
  }
  let totalBytes = 0;
  for (const file of files) {
    const fileName = String(file?.name || "attachment");
    const extensionIndex = fileName.lastIndexOf(".");
    const extension =
      extensionIndex >= 0 ? fileName.slice(extensionIndex).toLowerCase() : "";
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      localError(`${fileName} is not an allowed file type.`);
    }
    const sizeBytes = Number(file?.size || 0);
    if (!sizeBytes) {
      localError(`${fileName} is empty.`);
    }
    if (sizeBytes > MAX_FILE_BYTES) {
      localError(`${fileName} exceeds the 6 MB limit.`);
    }
    totalBytes += sizeBytes;
  }
  if (totalBytes > MAX_TOTAL_FILE_BYTES) {
    localError("Attachments cannot exceed 6 MB total per action.");
  }
}

function statusLabel(status) {
  return (
    SUPPORT_STATUS_LABELS[status] ||
    String(status || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}

function normalizeSeedTicket(seed, index) {
  const identity = localIdentity();
  const number = Number(seed.ticket_sequence || index + 1);
  const createdAt =
    seed.created_at ||
    new Date(Date.now() - (index + 1) * 3600000).toISOString();
  return {
    ticket_id: seed.ticket_id || localId(),
    ticket_sequence: number,
    company_alias: seed.company_alias || identity.company_alias,
    requester_user_id: seed.requester_user_id || identity.user_id,
    requester_login: seed.requester_login || identity.login,
    requester_display_name:
      seed.requester_display_name || identity.display_name,
    subject: seed.subject || `Sample support request ${number}`,
    description: seed.description || "Please review this request.",
    reported_environment: seed.reported_environment || "legacy",
    status: seed.status || "submitted",
    version: Number(seed.version || 1),
    created_at: createdAt,
    updated_at: seed.updated_at || createdAt,
    last_activity_at: seed.last_activity_at || createdAt,
    completed_at: seed.completed_at || "",
    technical_context: seed.technical_context || {
      frontend_version: "Not captured",
      proposal_api_version: "Not captured",
      support_api_version: "Not captured",
      support_lambda_version: "Not captured",
    },
  };
}

function ensureLocalStore() {
  if (localInitialized) return;
  localInitialized = true;
  const seed =
    typeof window !== "undefined" &&
    Array.isArray(window.__VICUS_SUPPORT_SEED__)
      ? window.__VICUS_SUPPORT_SEED__
      : [];
  localTickets = seed.map(normalizeSeedTicket);
  localSequence =
    localTickets.reduce(
      (highest, ticket) => Math.max(highest, ticket.ticket_sequence),
      0,
    ) + 1;
  localActivities = localTickets.map((ticket) => ({
    activity_id: localId(),
    ticket_id: ticket.ticket_id,
    activity_type: "created",
    actor_user_id: ticket.requester_user_id,
    actor_login: ticket.requester_login,
    actor_display_name: ticket.requester_display_name,
    actor_company_alias: ticket.company_alias,
    actor_role: "requester",
    body: "Support ticket submitted.",
    from_status: "",
    to_status: "submitted",
    metadata: { ticket_number: ticketNumber(ticket.ticket_sequence) },
    created_at: ticket.created_at,
  }));
  localAttachments = [];
}

export function resetSupportLocalStore(seed = []) {
  for (const url of localAttachmentUrls.values()) {
    URL.revokeObjectURL(url);
  }
  localAttachmentUrls.clear();
  remoteAttachmentUrls.clear();
  localTickets = [];
  localActivities = [];
  localAttachments = [];
  localSequence = 1;
  localInitialized = false;
  if (typeof window !== "undefined") {
    window.__VICUS_SUPPORT_SEED__ = seed;
  }
  ensureLocalStore();
}

function localCanManageTicket(ticket, identity) {
  return (
    identity.can_manage ||
    identity.can_collaborate ||
    ticket.requester_user_id === identity.user_id ||
    ticket.requester_login === identity.login
  );
}

function requireLocalAccess() {
  if (!localIdentity().has_access) {
    localError("Support access is not available for this account.", 403);
  }
}

function localActivity({
  ticket,
  type,
  body = "",
  fromStatus = "",
  toStatus = "",
  metadata = {},
}) {
  const identity = localIdentity();
  const item = {
    activity_id: localId(),
    ticket_id: ticket.ticket_id,
    activity_type: type,
    actor_user_id: identity.user_id,
    actor_login: identity.login,
    actor_display_name: identity.display_name,
    actor_company_alias: identity.company_alias,
    actor_role: identity.can_manage ? "support_agent" : "requester",
    body,
    from_status: fromStatus,
    to_status: toStatus,
    metadata: {
      ...metadata,
      technical_context: {
        ...clientDiagnostics(),
        reported_environment: config.supportApi.reportedEnvironment,
        support_api_version: "local",
        support_lambda_version: "local",
      },
    },
    created_at: nowIso(),
  };
  localActivities.push(item);
  return item;
}

function localAttachmentPayload(attachment) {
  return {
    attachment_id: attachment.attachment_id,
    activity_id: attachment.activity_id,
    file_name: attachment.file_name,
    content_type: attachment.content_type,
    size_bytes: attachment.size_bytes,
    sha256_hash: attachment.sha256_hash || "",
    uploaded_by_login: attachment.uploaded_by_login,
    uploaded_at: attachment.uploaded_at,
  };
}

function addLocalFiles(ticket, files = []) {
  const identity = localIdentity();
  for (const file of files) {
    const attachmentId = localId();
    const activity = localActivity({
      ticket,
      type: "attachment_added",
      body: `Added ${file.name}.`,
      metadata: {
        attachment_id: attachmentId,
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
        size_bytes: file.size,
      },
    });
    localAttachments.push({
      attachment_id: attachmentId,
      ticket_id: ticket.ticket_id,
      activity_id: activity.activity_id,
      file_name: file.name,
      content_type: file.type || "application/octet-stream",
      size_bytes: file.size,
      uploaded_by_login: identity.login,
      uploaded_at: activity.created_at,
      file,
    });
    localAttachmentUrls.set(attachmentId, URL.createObjectURL(file));
  }
}

function ticketPayload(ticket, includeDescription = false) {
  const identity = localIdentity();
  const attachments = localAttachments.filter(
    (item) => item.ticket_id === ticket.ticket_id,
  );
  const activities = localActivities.filter(
    (item) => item.ticket_id === ticket.ticket_id,
  );
  const isRequester =
    ticket.requester_user_id === identity.user_id ||
    ticket.requester_login === identity.login;
  const descriptionPreview =
    ticket.description.length <= 240
      ? ticket.description
      : `${ticket.description.slice(0, 237).trim()}...`;
  return {
    ticket_id: ticket.ticket_id,
    ticket_number: ticketNumber(ticket.ticket_sequence),
    company_alias: ticket.company_alias,
    requester_user_id: ticket.requester_user_id,
    requester_login: ticket.requester_login,
    requester_display_name: ticket.requester_display_name,
    subject: ticket.subject,
    reported_environment: ticket.reported_environment || "legacy",
    reported_environment_label:
      SUPPORT_ENVIRONMENT_LABELS[ticket.reported_environment] ||
      SUPPORT_ENVIRONMENT_LABELS.legacy,
    ...(includeDescription
      ? { description: ticket.description }
      : { description_preview: descriptionPreview }),
    status: ticket.status,
    status_label: statusLabel(ticket.status),
    version: ticket.version,
    created_at: ticket.created_at,
    updated_at: ticket.updated_at,
    last_activity_at: ticket.last_activity_at,
    completed_at: ticket.completed_at,
    attachment_count: attachments.length,
    activity_count: activities.length,
    is_requester: isRequester,
    can_manage: identity.can_manage,
    can_comment: ticket.status !== "closed",
    requester_can_review:
      isRequester && ticket.status === "waiting_for_user_review",
    ...(identity.can_manage
      ? { technical_context: ticket.technical_context }
      : {}),
  };
}

function activityPayload(activity) {
  return {
    ...activity,
    from_status_label: activity.from_status
      ? statusLabel(activity.from_status)
      : "",
    to_status_label: activity.to_status ? statusLabel(activity.to_status) : "",
  };
}

function localDetail(ticket) {
  return {
    ticket: ticketPayload(ticket, true),
    activity: localActivities
      .filter((item) => item.ticket_id === ticket.ticket_id)
      .sort((left, right) => left.created_at.localeCompare(right.created_at))
      .map(activityPayload),
    attachments: localAttachments
      .filter((item) => item.ticket_id === ticket.ticket_id)
      .sort((left, right) => left.uploaded_at.localeCompare(right.uploaded_at))
      .map(localAttachmentPayload),
  };
}

function requireLocalTicket(ticketId) {
  const identity = localIdentity();
  requireLocalAccess();
  const ticket = localTickets.find((item) => item.ticket_id === ticketId);
  if (!ticket || !localCanManageTicket(ticket, identity)) {
    localError("Support ticket not found.", 404);
  }
  return ticket;
}

function assertLocalVersion(ticket, expectedVersion) {
  if (ticket.version !== Number(expectedVersion)) {
    localError(
      "This support ticket changed since it was loaded. Refresh it and try again.",
      409,
    );
  }
}

async function getLocalProfile() {
  ensureLocalStore();
  await localWait();
  const identity = localIdentity();
  return {
    user_id: identity.user_id,
    has_access: identity.has_access,
    can_collaborate: identity.can_collaborate,
    can_manage: identity.can_manage,
    status_labels: SUPPORT_STATUS_LABELS,
    agent_statuses: AGENT_STATUSES.map((value) => ({
      value,
      label: SUPPORT_STATUS_LABELS[value],
    })),
    reported_environments: Object.entries(SUPPORT_ENVIRONMENT_LABELS).map(
      ([value, label]) => ({ value, label }),
    ),
    allowed_extensions: ALLOWED_EXTENSIONS,
    max_file_bytes: MAX_FILE_BYTES,
    max_total_file_bytes: MAX_TOTAL_FILE_BYTES,
    max_files_per_action: MAX_FILES_PER_ACTION,
  };
}

async function searchLocalTickets(payload = {}) {
  ensureLocalStore();
  await localWait();
  requireLocalAccess();
  const identity = localIdentity();
  const query = String(payload.query || "")
    .trim()
    .toLowerCase();
  const statuses = Array.isArray(payload.statuses)
    ? payload.statuses.filter((status) => SUPPORT_STATUS_LABELS[status])
    : [];
  const includeAll =
    (identity.can_manage || identity.can_collaborate) &&
    payload.scope === "all";
  const accessibleTickets = localTickets.filter(
    (ticket) =>
      includeAll ||
      ticket.requester_user_id === identity.user_id ||
      ticket.requester_login === identity.login,
  );
  const requesters = includeAll
    ? [
        ...new Map(
          accessibleTickets.map((ticket) => [
            ticket.requester_user_id,
            {
              user_id: ticket.requester_user_id,
              login: ticket.requester_login,
              display_name: ticket.requester_display_name,
              company_alias: ticket.company_alias,
            },
          ]),
        ).values(),
      ].sort((left, right) =>
        String(left.display_name || left.login).localeCompare(
          String(right.display_name || right.login),
        ),
      )
    : [];
  const requesterUserIds =
    includeAll && Array.isArray(payload.requester_user_ids)
      ? new Set(payload.requester_user_ids.map(String))
      : new Set();
  const reportedEnvironments =
    includeAll && Array.isArray(payload.reported_environments)
      ? new Set(
          payload.reported_environments.filter(
            (value) => SUPPORT_ENVIRONMENT_LABELS[value],
          ),
        )
      : new Set();
  const filteredTickets = accessibleTickets
    .filter(
      (ticket) =>
        !requesterUserIds.size ||
        requesterUserIds.has(String(ticket.requester_user_id)),
    )
    .filter(
      (ticket) =>
        !reportedEnvironments.size ||
        reportedEnvironments.has(ticket.reported_environment || "legacy"),
    )
    .filter((ticket) =>
      statuses.length
        ? statuses.includes(ticket.status)
        : payload.include_completed ||
          !["completed", "closed"].includes(ticket.status),
    )
    .filter((ticket) => {
      if (!query) return true;
      return [
        ticketNumber(ticket.ticket_sequence),
        ticket.subject,
        ticket.description,
        ticket.requester_display_name,
        ticket.requester_login,
        SUPPORT_ENVIRONMENT_LABELS[ticket.reported_environment || "legacy"],
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      );
    });
  const statusCounts = Object.fromEntries(
    Object.keys(SUPPORT_STATUS_LABELS).map((status) => [status, 0]),
  );
  for (const ticket of filteredTickets) {
    if (Object.hasOwn(statusCounts, ticket.status)) {
      statusCounts[ticket.status] += 1;
    }
  }
  const items = filteredTickets
    .sort(compareSupportTickets)
    .slice(0, Math.max(1, Math.min(Number(payload.limit || 100), 200)));
  return {
    can_manage: identity.can_manage,
    scope: includeAll ? "all" : "mine",
    total_count: filteredTickets.length,
    requesters,
    status_counts: statusCounts,
    tickets: items.map((ticket) => ticketPayload(ticket)),
  };
}

async function getLocalTicket(ticketId) {
  ensureLocalStore();
  await localWait();
  requireLocalAccess();
  return clone(localDetail(requireLocalTicket(ticketId)));
}

async function createLocalTicket({ subject, description, files = [] }) {
  ensureLocalStore();
  await localWait();
  requireLocalAccess();
  const subjectValue = String(subject || "").trim();
  const descriptionValue = String(description || "").trim();
  if (!subjectValue) localError("Subject is required.");
  if (!descriptionValue) localError("Description is required.");
  if (subjectValue.length > 180) {
    localError("Subject cannot exceed 180 characters.");
  }
  if (descriptionValue.length > 20000) {
    localError("Description cannot exceed 20,000 characters.");
  }
  const identity = localIdentity();
  const timestamp = nowIso();
  const ticket = {
    ticket_id: localId(),
    ticket_sequence: localSequence,
    company_alias: identity.company_alias,
    requester_user_id: identity.user_id,
    requester_login: identity.login,
    requester_display_name: identity.display_name,
    subject: subjectValue,
    description: descriptionValue,
    reported_environment: config.supportApi.reportedEnvironment,
    status: "submitted",
    version: 1,
    created_at: timestamp,
    updated_at: timestamp,
    last_activity_at: timestamp,
    completed_at: "",
    technical_context: {
      ...clientDiagnostics(),
      support_api_version: "local",
      support_lambda_version: "local",
    },
  };
  localSequence += 1;
  localTickets.push(ticket);
  localActivity({
    ticket,
    type: "created",
    body: "Support ticket submitted.",
    toStatus: "submitted",
    metadata: { ticket_number: ticketNumber(ticket.ticket_sequence) },
  });
  addLocalFiles(ticket, files);
  return clone(localDetail(ticket));
}

async function addLocalComment({
  ticketId,
  message,
  expectedVersion,
  files = [],
}) {
  ensureLocalStore();
  await localWait();
  const ticket = requireLocalTicket(ticketId);
  assertLocalVersion(ticket, expectedVersion);
  if (ticket.status === "closed") {
    localError("Closed support tickets cannot be changed.", 409);
  }
  const body = String(message || "").trim();
  if (!body && !files.length) {
    localError("Add a comment or at least one attachment.");
  }
  if (body) {
    localActivity({ ticket, type: "comment", body });
  }
  addLocalFiles(ticket, files);
  ticket.version += 1;
  ticket.updated_at = nowIso();
  ticket.last_activity_at = ticket.updated_at;
  return clone(localDetail(ticket));
}

async function updateLocalStatus(payload) {
  ensureLocalStore();
  await localWait();
  const identity = localIdentity();
  if (!identity.can_manage) {
    localError("Support-agent access is required.", 403);
  }
  const ticket = requireLocalTicket(payload.ticket_id);
  assertLocalVersion(ticket, payload.expected_version);
  const targetStatus = String(payload.status || "");
  if (!AGENT_STATUSES.includes(targetStatus)) {
    localError("Invalid support ticket status.");
  }
  if (targetStatus === ticket.status) {
    localError("Select a different status.");
  }
  const message = String(payload.message || "").trim();
  if (targetStatus === "waiting_for_user_review" && !message) {
    localError("Explain what was fixed before requesting user review.");
  }
  const previousStatus = ticket.status;
  ticket.status = targetStatus;
  ticket.version += 1;
  ticket.updated_at = nowIso();
  ticket.last_activity_at = ticket.updated_at;
  ticket.completed_at = "";
  localActivity({
    ticket,
    type: "status_change",
    body: message,
    fromStatus: previousStatus,
    toStatus: targetStatus,
  });
  return clone(localDetail(ticket));
}

async function reviewLocalTicket(payload) {
  ensureLocalStore();
  await localWait();
  const identity = localIdentity();
  const ticket = requireLocalTicket(payload.ticket_id);
  if (ticket.requester_user_id !== identity.user_id) {
    localError(
      "Only the user who submitted this request can review the fix.",
      403,
    );
  }
  assertLocalVersion(ticket, payload.expected_version);
  if (ticket.status !== "waiting_for_user_review") {
    localError("This support ticket is not waiting for user review.", 409);
  }
  const message = String(payload.message || "").trim();
  if (payload.outcome === "not_fixed" && !message) {
    localError("Describe what is still incorrect so work can continue.");
  }
  const targetStatus = payload.outcome === "fixed" ? "completed" : "not_fixed";
  const previousStatus = ticket.status;
  ticket.status = targetStatus;
  ticket.version += 1;
  ticket.updated_at = nowIso();
  ticket.last_activity_at = ticket.updated_at;
  ticket.completed_at = targetStatus === "completed" ? ticket.updated_at : "";
  localActivity({
    ticket,
    type: "requester_review",
    body:
      message ||
      (targetStatus === "completed" ? "The requester confirmed the fix." : ""),
    fromStatus: previousStatus,
    toStatus: targetStatus,
    metadata: { outcome: payload.outcome },
  });
  return clone(localDetail(ticket));
}

function authHeaders(session) {
  if (!session.tokens?.idToken) return {};
  return {
    Authorization: `Bearer ${session.tokens.idToken.toString()}`,
  };
}

async function filePayload(file) {
  const dataBase64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () =>
      resolve(
        String(reader.result || "")
          .split(",")
          .pop() || "",
      );
    reader.readAsDataURL(file);
  });
  return {
    file_name: file.name,
    content_type: file.type || "application/octet-stream",
    size_bytes: file.size,
    data_base64: dataBase64,
  };
}

async function remoteRequest(action, payload = {}) {
  const session = await fetchAuthSession();
  const response = await post({
    apiName: API_NAME,
    path: SUPPORT_PATH,
    options: {
      headers: authHeaders(session),
      body: { action, payload },
    },
  }).response;
  const result = await response.body.json();
  if (result?.error) {
    const error = new Error(result.error);
    error.status = result.statusCode || 400;
    throw error;
  }
  const data =
    typeof result?.body === "string"
      ? JSON.parse(result.body)
      : result?.body || result;
  if (data?.error) {
    const error = new Error(data.error);
    error.status = data.statusCode || result?.statusCode || 400;
    throw error;
  }
  for (const attachment of data?.attachments || []) {
    if (attachment.download_url) {
      remoteAttachmentUrls.set(
        attachment.attachment_id,
        attachment.download_url,
      );
    }
  }
  return action === "profile"
    ? {
        ...data,
        user_id: String(session.tokens?.idToken?.payload?.sub || ""),
      }
    : data;
}

export function getSupportProfile() {
  return isLocalMode() ? getLocalProfile() : remoteRequest("profile");
}

export function searchSupportTickets(payload = {}) {
  return isLocalMode()
    ? searchLocalTickets(payload)
    : remoteRequest("search", payload);
}

export function getSupportTicket(ticketId) {
  return isLocalMode()
    ? getLocalTicket(ticketId)
    : remoteRequest("detail", { ticket_id: ticketId });
}

export async function createSupportTicket({
  subject,
  description,
  files = [],
}) {
  validateFiles(files);
  if (isLocalMode()) {
    return createLocalTicket({ subject, description, files });
  }
  return remoteRequest("create", {
    subject,
    description,
    reported_environment: config.supportApi.reportedEnvironment,
    technical_context: clientDiagnostics(),
    files: await Promise.all(files.map(filePayload)),
  });
}

export async function addSupportTicketComment({
  ticketId,
  message,
  expectedVersion,
  files = [],
}) {
  validateFiles(files);
  if (isLocalMode()) {
    return addLocalComment({
      ticketId,
      message,
      expectedVersion,
      files,
    });
  }
  return remoteRequest("comment", {
    ticket_id: ticketId,
    message,
    expected_version: expectedVersion,
    technical_context: clientDiagnostics(),
    files: await Promise.all(files.map(filePayload)),
  });
}

export function updateSupportTicketStatus(payload) {
  return isLocalMode()
    ? updateLocalStatus(payload)
    : remoteRequest("status", {
        ...payload,
        technical_context: clientDiagnostics(),
      });
}

export function reviewSupportTicket(payload) {
  return isLocalMode()
    ? reviewLocalTicket(payload)
    : remoteRequest("review", {
        ...payload,
        technical_context: clientDiagnostics(),
      });
}

export function supportAttachmentUrl(attachmentId) {
  return (
    localAttachmentUrls.get(attachmentId) ||
    remoteAttachmentUrls.get(attachmentId) ||
    "#"
  );
}
